/**
 * F3-ServiceType — the router. Bathroom / kitchen / paint / something else.
 * Decides every question downstream.
 */
import { useLocation, useNavigate } from 'react-router-dom';

interface ServiceChoice {
  code: string;
  label: string;
  sublabel: string;
}

interface ServiceState {
  masterId?: string;
}

const CHOICES: ServiceChoice[] = [
  { code: 'BATHROOM', label: 'A bathroom', sublabel: 'Refresh, full rebuild, or a walk-in shower conversion' },
  { code: 'KITCHEN', label: 'A kitchen', sublabel: 'Cabinets, countertops, backsplash, appliances' },
  { code: 'PAINT', label: 'Paint or drywall', sublabel: 'Rooms, whole house, repairs, interior or exterior' },
  { code: 'OTHER', label: 'Something else', sublabel: 'Tell us what you\u2019re picturing and we\u2019ll point you the right way' },
];

export function ServiceType(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { masterId } = (location.state as ServiceState) ?? {};

  function choose(code: string): void {
    if (code === 'BATHROOM') {
      // Name the project, then scope the first bathroom. More can be added later.
      navigate('/design/project-start', {
        state: { serviceLine: 'BATHROOM', productCode: 'BATH_FULL', ...(masterId ? { masterId } : {}) },
      });
    } else if (code === 'KITCHEN') {
      navigate('/design/project-start', {
        state: { serviceLine: 'KITCHEN', productCode: 'KITCHEN_FULL', ...(masterId ? { masterId } : {}) },
      });
    } else if (code === 'PAINT') {
      // Paint asks interior/exterior/both before naming the project.
      navigate('/design/paint', { state: { ...(masterId ? { masterId } : {}) } });
    } else {
      // "Something else" (and any multi-line request) → book a call.
      navigate('/talk');
    }
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>What are we working on?</h1>
        <p className="stage-card__lede">
          Pick the closest fit. It decides the questions we&rsquo;ll ask &mdash; every room is
          scoped its own way.
        </p>
        <div className="options" role="radiogroup" aria-label="What are we working on?">
          {CHOICES.map((c) => (
            <button
              key={c.code}
              type="button"
              className="pb-option"
              role="radio"
              aria-checked={false}
              onClick={() => choose(c.code)}
            >
              <span style={{ flexGrow: 1 }}>
                <span className="pb-option__label">{c.label}</span>
                <span className="pb-option__sub">{c.sublabel}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
